import { fetchApi } from '@/config/api';
import { Brand, VendeurMarqueApi } from '@/types';
import { useAuthStore } from '@/stores/auth';


/**
 * Récupérer toutes les marques du vendeur connecté
 */
export async function getVendeurMarques(): Promise<Brand[]> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur

  if (!vendeurId) {
    throw new Error('Vendeur non connecté')
  }

  const query = `
    query GetVendeurMarquesByVendeur($id_vendeur: Int!) {
      vendeurMarquesByVendeur(id_vendeur: $id_vendeur) {
        id_vendeur_marque
        id_vendeur
        nom_marque
        defaut
        supprimer
        statut
        nombre_produits
      }
    }
  `

  const result = await fetchApi<{ vendeurMarquesByVendeur: VendeurMarqueApi[] }>(
      query,
      { id_vendeur: vendeurId },
  )

  const marques = result.vendeurMarquesByVendeur
      .filter(marque => marque.supprimer === 0)
      .map(marque => ({
        id: marque.id_vendeur_marque.toString(),
        number: marque.id_vendeur_marque,
        name: marque.nom_marque,
        isDefault: marque.defaut === 1,
        isActive: marque.statut === 1,
        productCount: marque.nombre_produits ?? 0,
      }))

  return marques
}

// Désactiver toutes les marques par défaut d'un vendeur
async function disableAllDefaultBrands(vendeurId: number): Promise<void> {
  const mutation = `
    mutation UpdateVendeurMarque($input: UpdateVendeurMarqueInput!) {
      updateVendeurMarque(input: $input) {
        id_vendeur_marque
        defaut
      }
    }
  `;

  // Récupérer toutes les marques du vendeur
  const query = `
    query GetVendeurMarques {
      vendeursMarques {
        id_vendeur_marque
        id_vendeur
        defaut
        supprimer
      }
    }
  `;

  const result = await fetchApi<{ vendeursMarques: VendeurMarqueApi[] }>(query);
  const marquesVendeur = result.vendeursMarques.filter(marque => 
    marque.id_vendeur === vendeurId && marque.defaut === 1 && marque.supprimer === 0
  );

  // Désactiver toutes les marques par défaut
  for (const marque of marquesVendeur) {
    await fetchApi(mutation, { 
      input: { 
        id_vendeur_marque: marque.id_vendeur_marque, 
        defaut: 0 
      } 
    });
  }
}

// Créer une nouvelle marque
export async function createVendeurMarque(nomMarque: string, isDefault = false): Promise<Brand> {
  const authStore = useAuthStore();
  const vendeurId = authStore.user?.id_vendeur;
  
  if (!vendeurId) {
    throw new Error('Vendeur non connecté');
  }

  // Si c'est une marque par défaut, désactiver toutes les autres d'abord
  if (isDefault) {
    await disableAllDefaultBrands(vendeurId);
  }

  const mutation = `
    mutation CreateVendeurMarque($input: CreateVendeurMarqueInput!) {
      createVendeurMarque(input: $input) {
        id_vendeur_marque
        id_vendeur
        nom_marque
        defaut
        supprimer
      }
    }
  `;

  const input = {
    id_vendeur: vendeurId,
    nom_marque: nomMarque,
    defaut: isDefault ? 1 : 0,
    supprimer: 0
  };

  const result = await fetchApi<{ createVendeurMarque: VendeurMarqueApi }>(mutation, { input });
  
  return {
    id: result.createVendeurMarque.id_vendeur_marque.toString(),
    number: result.createVendeurMarque.id_vendeur_marque,
    name: result.createVendeurMarque.nom_marque,
    isDefault: result.createVendeurMarque.defaut === 1,
    isActive: false,
  };
}


// Modifier une marque existante
export async function updateVendeurMarque(id: string, nomMarque?: string, isDefault?: boolean): Promise<Brand> {
  const authStore = useAuthStore();
  const vendeurId = authStore.user?.id_vendeur;
  
  if (!vendeurId) {
    throw new Error('Vendeur non connecté');
  }

  // Si c'est une marque par défaut, désactiver toutes les autres d'abord
  if (isDefault === true) {
    await disableAllDefaultBrands(vendeurId);
  }

  const mutation = `
    mutation UpdateVendeurMarque($input: UpdateVendeurMarqueInput!) {
      updateVendeurMarque(input: $input) {
        id_vendeur_marque
        id_vendeur
        nom_marque
        defaut
        supprimer
      }
    }
  `;

  // Construire l'input dynamiquement pour n'envoyer que les champs à modifier
  const input: Record<string, any> = {
    id_vendeur_marque: parseInt(id)
  };
  if (typeof nomMarque !== 'undefined') {
    input.nom_marque = nomMarque;
  }
  if (typeof isDefault !== 'undefined') {
    input.defaut = isDefault ? 1 : 0;
  }

  const result = await fetchApi<{ updateVendeurMarque: VendeurMarqueApi }>(mutation, { input });
  
  return {
    id: result.updateVendeurMarque.id_vendeur_marque.toString(),
    number: result.updateVendeurMarque.id_vendeur_marque,
    name: result.updateVendeurMarque.nom_marque,
    isDefault: result.updateVendeurMarque.defaut === 1,
    isActive: false,
  };
}

export async function activerVendeurMarques(): Promise<{
  statutBoutique: number
  nombreActivees: number
}> {
  const authStore = useAuthStore()
  if (!authStore.user?.id_vendeur) {
    throw new Error('Vendeur non connecté')
  }

  const mutation = `
    mutation ActiverVendeurMarques {
      activerVendeurMarques {
        statutBoutique
        nombreActivees
      }
    }
  `

  const result = await fetchApi<{
    activerVendeurMarques: { statutBoutique: number; nombreActivees: number }
  }>(mutation)

  return result.activerVendeurMarques
}

export async function setVendeurMarqueStatut(
  id: string,
  actif: boolean,
): Promise<void> {
  const authStore = useAuthStore()
  if (!authStore.user?.id_vendeur) {
    throw new Error('Vendeur non connecté')
  }

  const mutation = `
    mutation SetVendeurMarqueStatut($id: Int!, $actif: Boolean!) {
      setVendeurMarqueStatut(id_vendeur_marque: $id, actif: $actif) {
        id_vendeur_marque
        statut
      }
    }
  `

  await fetchApi(mutation, { id: parseInt(id, 10), actif })
}

// Supprimer une marque (soft delete) avec réassignation optionnelle des produits
export async function deleteVendeurMarque(
  id: string,
  targetBrandId?: string,
): Promise<boolean> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id_vendeur

  if (!vendeurId) {
    throw new Error('Vendeur non connecté')
  }

  // Sécurité côté client: empêcher la suppression de la marque par défaut
  try {
    const marques = await getVendeurMarques()
    const cible = marques.find(m => m.id === id)
    if (cible?.isDefault) {
      throw new Error("La marque par défaut ne peut pas être supprimée")
    }
    if ((cible?.productCount ?? 0) > 0 && !targetBrandId) {
      throw new Error('Veuillez choisir une marque de destination pour les produits concernés.')
    }
  } catch (e) {
    if (e instanceof Error && (
      e.message === "La marque par défaut ne peut pas être supprimée" ||
      e.message === 'Veuillez choisir une marque de destination pour les produits concernés.'
    )) {
      throw e
    }
    throw e instanceof Error ? e : new Error('Impossible de vérifier la marque à supprimer')
  }

  const mutation = `
    mutation DeleteVendeurMarqueWithReassign($input: DeleteVendeurMarqueInput!) {
      deleteVendeurMarqueWithReassign(input: $input)
    }
  `

  const input: Record<string, number> = {
    id_vendeur: vendeurId,
    id_vendeur_marque: parseInt(id),
  }
  if (targetBrandId) {
    input.id_vendeur_marque_cible = parseInt(targetBrandId)
  }

  const result = await fetchApi<{ deleteVendeurMarqueWithReassign: boolean }>(
    mutation,
    { input },
  )
  return result.deleteVendeurMarqueWithReassign
}
